/* ==========================================================================
   CalendarRxjs > Client > Prototype
   ========================================================================== */

/* Modules */
const flatpickr = require('flatpickr').default;
const ConfirmDatePlugin = require('flatpickr/dist/plugins/confirmDate/confirmDate');

const { Store } = require('@bluekiri/alexandria/helpers/state');

const { fromEvent, of } = require('rxjs');
const { switchMap, filter } = require('rxjs/operators');

const { actions, types } = require('./store/actions');
const { reducer } = require('./store/reducer');

const { addClear, destroyClear } = require('./clear');

const { isMobile, isModeRange, isModeSingle, isModeTime } = require('./helpers');

/**
 * Set language
 * Imports and configure the language locale
 * to the flatpickr library with its l10n files
 * @param {string} Language
 */
function setLanguage(language) {
    const { Spanish } = module.require('flatpickr/dist/l10n/es');
    const { German } = module.require('flatpickr/dist/l10n/de');
    const { French } = module.require('flatpickr/dist/l10n/fr');
    const { Russian } = module.require('flatpickr/dist/l10n/ru');
    const { Portuguese } = module.require('flatpickr/dist/l10n/pt');
    const { Finnish } = module.require('flatpickr/dist/l10n/fi');
    const { Italian } = module.require('flatpickr/dist/l10n/it');

    this.languages = {
        es: Spanish,
        pt: Portuguese,
        it: Italian,
        de: German,
        fr: French,
        br: Portuguese,
        fi: Finnish,
        mx: Spanish,
        ru: Russian
    };

    flatpickr.localize(this.languages[language.toLowerCase()]);
}

/**
 * Set flatpickr range
 */
function flatpickrRangeConfig(data = {}) {
    const { dates = [] } = data;

    return {
        defaultDate: dates,
        mode: 'range'
    };
}

/**
 * Set flatpickr single
 */
function flatpickrSingleConfig(data = {}) {
    const { dates = [] } = data;

    return {
        defaultDate: dates.slice(0, 1),
        mode: 'single'
    };
}

/**
 * Set flatpickr time
 */
function flatpickrTimeConfig(data = {}) {
    const { dates = [], minTime, maxTime } = data;

    return {
        dateFormat: 'D, j M Y H:i',
        defaultDate: dates.slice(0, 1),
        enableTime: true,
        minuteIncrement: 30,
        mode: 'single',
        noCalendar: false,
        showMonths: 1,
        time_24hr: true,
        minTime,
        maxTime,
        plugins: [
            new ConfirmDatePlugin({
                confirmIcon: '',
                confirmText: 'OK'
            })
        ]
    };
}

/**
 * Resolver flatpickr config
 */
function resolveFlatpickrConfig(data = {}, mode = '') {
    if (!isModeRange(mode) && !isModeSingle(mode) && !isModeTime(mode)) {
        return flatpickrRangeConfig(data);
    }

    if (isModeRange(mode)) return flatpickrRangeConfig(data);

    if (isModeSingle(mode)) return flatpickrSingleConfig(data);

    return flatpickrTimeConfig(data);
}

/* Prototype */
const prototype = {
    /* Setup flatpickr */
    setupFlatpickr(params = {}) {
        /* Data */
        const { context = {} } = this.config;
        const { device, language } = context;

        const {
            minTime,
            maxTime,
            minDate,
            maxDate,
            mode,
            disable = [],
            showMonths = isMobile(device) ? 1 : 2
        } = this.config;

        /* Config */
        const config = {
            showMonths,
            ...resolveFlatpickrConfig(
                {
                    minTime,
                    maxTime
                },
                mode
            ),
            ...params
        };

        /* Setup language locale */
        setLanguage(language);

        const disableParsed = disable
            ? disable.map(d => {
                  return Object.keys(d)
                      .map((k, i) => {
                          return {
                              [k]: new Date(Object.values(d)[i])
                          };
                      })
                      .reduce((a, b) => ({ ...a, ...b }));
              })
            : [];

        this.fp = flatpickr(this.hooks.input, {
            init: true,
            dateFormat: 'D, j M Y',
            locale: {
                rangeSeparator: ' → ',
                firstDayOfWeek: 1 // start week on Monday
            },
            minDate: !minDate ? 'today' : new Date(minDate),
            maxDate: !maxDate ? new Date().fp_incr(365 * 2) : new Date(maxDate),
            static: true,
            disableMobile: true,
            disable: disableParsed,
            ...this.config.fp,
            ...config
        });
    },
    /* Setup store */
    setupStore() {
        if (this.store) return this;

        this.store = new Store({
            calendar: reducer
        });

        return this;
    },
    /* Setup actions */
    setupActions() {
        if (this.actions) return this;

        this.actions = {
            destroy: () => this.store.dispatch(actions.destroy()),
            disable: () => this.store.dispatch(actions.disable()),
            display: () => this.store.dispatch(actions.display()),
            enable: () => this.store.dispatch(actions.enable()),
            hide: () => this.store.dispatch(actions.hide()),
            resetStatus: () => this.store.dispatch(actions.resetStatus()),
            setError: () => this.store.dispatch(actions.setError()),
            setMode: (mode = String) => this.store.dispatch(actions.setMode(mode)),
            setValid: () => this.store.dispatch(actions.setValid()),
            toggleActive: () => this.store.dispatch(actions.toggleActive()),
            setValue: value => this.store.dispatch(actions.setValue(value)),
            changeValue: value => this.store.dispatch(actions.changeValue(value))
        };

        return this;
    },
    /* Setup reactions */
    setupReactions() {
        if (this.reactions) return this;

        this.reactions = {
            destroy: this.store.subscribe((state, action) => {
                if (action.type !== types.DESTROY) return;

                /* Data */
                const { hasClear } = this.config;

                /* Flatpickr destroy */
                this.fp.destroy();

                /* Clear destroy */
                if (hasClear) {
                    if (this.clear) {
                        destroyClear(this);
                    }
                }

                /* Unsubscribe */
                this.unsubscribe();

                /* Remove from DOM */
                this.element.parentNode.removeChild(this.element);

                /* Clean instance */
                [...Object.keys(this)].forEach(key => delete this[key]);
            }),
            disable: this.store.subscribe((state, action) => {
                if (action.type !== types.DISABLE) return;

                const { css } = this.config;
                this.element.classList.add(css.disabled);
                this.hooks.input.setAttribute('disabled', '');
            }),
            display: this.store.subscribe((state, action) => {
                if (action.type !== types.DISPLAY) return;

                const { css } = this.config;
                this.element.classList.remove(css.hidden);
            }),
            enable: this.store.subscribe((state, action) => {
                if (action.type !== types.ENABLE) return;

                const { css } = this.config;
                this.element.classList.remove(css.disabled);
                this.hooks.input.removeAttribute('disabled');
            }),
            hide: this.store.subscribe((state, action) => {
                if (action.type !== types.HIDE) return;

                const { css } = this.config;
                this.element.classList.add(css.hidden);
            }),
            resetStatus: this.store.subscribe((state, action) => {
                if (action.type !== types.RESET_STATUS) return;

                const { css } = this.config;
                this.element.classList.remove(css.valid);
                this.element.classList.remove(css.error);
            }),
            setError: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_ERROR) return;

                const { css } = this.config;
                this.element.classList.remove(css.valid);
                this.element.classList.add(css.error);
            }),
            setMode: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_MODE) return;

                if (!isModeRange(action.payload) && !isModeSingle(action.payload) && !isModeTime(action.payload)) {
                    return;
                }

                /* Current dates to pass to new instance */
                const dates = this.fp.selectedDates || [];

                const { minTime, maxTime, disable } = this.config;

                /* Destroy current flatpickr */
                this.fp.destroy();

                /* Recreate fp with new config */
                this.setupFlatpickr(
                    resolveFlatpickrConfig(
                        {
                            minTime,
                            maxTime,
                            disable,
                            dates
                        },
                        action.payload
                    )
                );
            }),
            setValid: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_VALID) return;

                const { css } = this.config;
                this.element.classList.remove(css.error);
                this.element.classList.add(css.valid);
            }),
            toggleActive: this.store.subscribe((state, action) => {
                if (action.type !== types.TOGGLE_ACTIVE) return;

                const { active } = this.store.select('calendar');
                const { css } = this.config;

                if (active) {
                    this.element.classList.add(css.active);
                } else {
                    this.element.classList.remove(css.active);
                }
            }),
            setValue: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_VALUE) return;

                /* Data */
                const { active } = state.calendar;
                const { isAlternative, hasClear } = this.config;

                /* Set data to the library */
                this.fp.setDate(action.payload);

                /* Active */
                if (!isAlternative && !active) this.actions.toggleActive();

                /* Clear */
                if (!hasClear) return;

                if (!this.clear) {
                    addClear(this);
                }
            }),
            changeValue: this.store.subscribe((state, action) => {
                if (action.type !== types.CHANGE_VALUE) return;

                /* Data */
                const { active } = state.calendar;
                const { isAlternative, hasClear } = this.config;

                /* Active */
                if (!isAlternative && !active) this.actions.toggleActive();

                /* Clear */
                if (!hasClear) return;

                if (!this.clear) {
                    addClear(this);
                }
            })
        };

        return this;
    },
    /* Subscribe */
    subscribe() {
        if (this.subscriptions) this.unsubscribe();

        this.subscriptions = {
            focusIn$: fromEvent(this.element, 'focusin').subscribe(() => {
                const { active } = this.store.select('calendar');

                if (!active && !this.config.isAlternative) this.actions.toggleActive();
            }),
            focusOut$: fromEvent(this.element, 'focusout').subscribe(() => {
                const { active } = this.store.select('calendar');
                const { value } = this.hooks.input;

                if (active && !value && !this.config.isAlternative) this.actions.toggleActive();
            }),
            change$: fromEvent(this.element, 'change')
                .pipe(
                    switchMap(() => of(this.fp.selectedDates)),
                    filter(data => {
                        const { mode } = this.store.select('calendar');

                        if (isModeRange(mode)) {
                            return data.length > 1;
                        }

                        return data.length === 1;
                    })
                )
                .subscribe(data => {
                    /* Dispatch update store */
                    this.actions.changeValue(data);
                })
        };
    },
    /* Unsubscribe */
    unsubscribe() {
        if (!this.subscriptions) return this;

        [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());

        return this;
    },
    /* Start */
    start() {
        if (!this.config.start) return this;

        /* Init data */
        const { startDate, endDate, mode = 'range' } = this.config;

        /* Set mode */
        this.actions.setMode(mode);

        if (!startDate && !endDate) return this;

        /* Start dates */
        if (isModeRange(mode)) {
            this.actions.setValue([new Date(startDate), new Date(endDate)]);
        } else {
            this.actions.setValue(new Date(startDate));
        }

        return this;
    },
    /* Init */
    init() {
        /* Setup flatpickr */
        this.setupFlatpickr();

        /* Setup store */
        this.setupStore();

        /* Setup actions */
        this.setupActions();

        /* Setup reactions */
        this.setupReactions();

        /* Subscribe */
        this.subscribe();

        /* Start */
        this.start();
    }
};

/* Exports */
module.exports = prototype;
